import React, { useRef } from 'react';
import { useCombobox } from '@indeed/downshift';
import isFunction from '../../utils/isFunction.js';
import { FORM_SIZES } from '../../constants/index.js';
import { ComboboxProvider } from './useComboboxContext.js';
import { comboboxCollection } from './utils.js';
import warnDeprecation from '../../utils/warnDeprecation.js';
import { jsx } from '@emotion/react';
import '../Collection/Collection.js';
import '../../utils/mergeRefs.js';
import '../../utils/warn.js';

const COMBOBOX_SIZES = FORM_SIZES;
/**
 * Combobox
 *
 * Expects `ComboboxInput` and `ComboboxPopover` components as children.
 *
 * @see https://experience.indeed.design/components/ids/combobox
 *
 * @deprecated The `Combobox` component will be removed in IFL 7.
 * See the IFL 7 preparation guide for more information: https://link.indeed.tech/ifl-7-prep-guide
 */

const Combobox = props => {
  warnDeprecation(deprecationWarning);
  const {
    CollectionProvider
  } = comboboxCollection;
  return jsx(CollectionProvider, null, jsx(ComboboxImpl, props));
};
Combobox.displayName = "Combobox";
const ComboboxImpl = props => {
  const {
    children,
    disabled = false,
    filter,
    id,
    invalid = false,
    onClose,
    onOpen,
    onSelect,
    useComboboxProps = {}
  } = props;
  const [items, setItems] = React.useState([]);
  const inputWrapperRef = useRef(null);
  const {
    useCollection
  } = comboboxCollection;
  const {
    getItems
  } = useCollection();
  const [selectedValue, setSelectedValue] = React.useState("");
  const comboboxSelection = useCombobox({
    id,
    inputId: id,
    items,
    /**
     * Remove the default live region behavior when opened. From the below
     * GitHub issue:
     *
     * "Screen reader users can usually turn off the help/hint text for their
     * screen reader in order to reduce verbosity, and since they wouldn't be
     * able to turn this off, it would get annoying pretty quickly."
     *
     * See below for more information.
     * @see https://github.com/downshift-js/downshift/issues/1244
     * @see https://github.com/carbon-design-system/carbon/pull/7777#issuecomment-783863657
     *
     * Unfortunately, this does not actually remove the element the live region
     * is attached to.
     */
    getA11yStatusMessage: () => "",
    getA11ySelectionMessage: () => "",
    onStateChange: () => {
      setItems(getItems().map(item => item.value));
    },
    onSelectedItemChange: _ref => {
      let {
        selectedItem
      } = _ref;
      if (onSelect && selectedItem) {
        onSelect(selectedItem, context);
        setSelectedValue(selectedItem);
      }
    },
    onIsOpenChange: _ref2 => {
      let {
        isOpen
      } = _ref2;
      if (onClose && !isOpen) {
        onClose();
      }
      if (onOpen && isOpen) {
        onOpen();
      }
    },
    ...useComboboxProps
  });
  const context = {
    disabled,
    filter,
    inputWrapperRef,
    invalid,
    items,
    setItems,
    selectedValue,
    setSelectedValue,
    ...comboboxSelection
  };
  return jsx(ComboboxProvider, {
    value: context
  }, isFunction(children) ? children(context) : children);
};
const deprecationWarning = `[Combobox] The Combobox component will be removed in IFL 7. See the IFL 7 preparation guide for more information: https://link.indeed.tech/ifl-7-prep-guide`;

export { COMBOBOX_SIZES, Combobox };
